import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    // Quick-add can reconnect the same form while morphing its content.
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
this.addEventListener('quantity-selector:update', this.#scheduleMadeToOrderSync, { signal });
this.#scheduleMadeToOrderSync();
    const extras = this.querySelector('[data-ra-addons]');
    if (extras) { extras.hidden = false; this.handleAddonChange(); }
  }
#scheduleMadeToOrderSync = () => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      this.#syncMadeToOrderNotice();
    });
  });
};
#syncMadeToOrderNotice = () => {
const currentRoot = this.closest('.shopify-section, dialog, product-card') ?? document;
const notice = currentRoot.querySelector('[data-made-to-order-notice]');
  if (!notice) return;

  const inventoryQuantity = Number(notice.dataset.inventoryQuantity ?? 0);
  const inventoryPolicy = notice.dataset.inventoryPolicy;
  const inventoryManagement = notice.dataset.inventoryManagement;

const quantityInput = this.querySelector('quantity-selector-component input[ref="quantityInput"]');
const requestedQuantity = Number(quantityInput?.value ?? 1);

  const isMadeToOrder =
    inventoryManagement === 'shopify' &&
    inventoryPolicy === 'continue' &&
    requestedQuantity > inventoryQuantity;
const message = notice.querySelector('[data-made-to-order-message]');

if (isMadeToOrder && message) {
  const inStockQuantity = Math.max(0, inventoryQuantity);
  const madeToOrderQuantity = requestedQuantity - inStockQuantity;

if (inStockQuantity === 0) {
  message.textContent =
    `${madeToOrderQuantity} blanket${madeToOrderQuantity === 1 ? '' : 's'} ` +
    `${madeToOrderQuantity === 1 ? 'will' : 'will'} be made to order — ` +
    `created especially for you in about 3 weeks.`;
} else {
  message.textContent =
    `${inStockQuantity} blanket${inStockQuantity === 1 ? '' : 's'} ` +
    `${inStockQuantity === 1 ? 'is' : 'are'} ready to ship in 2–3 business days. ` +
    `The additional ${madeToOrderQuantity} ` +
    `blanket${madeToOrderQuantity === 1 ? '' : 's'} will be made to order — ` +
    `created especially for you in about 3 weeks.`;
}
}
notice.toggleAttribute('hidden', !isMadeToOrder);
const isEntirelyMadeToOrder =
  isMadeToOrder && inventoryQuantity <= 0;

const purchaseButtonText = isEntirelyMadeToOrder
  ? 'Made to Order'
  : 'Add to cart';

const mainAddToCartText = this.querySelector(
  '.add-to-cart-button .add-to-cart-text__content > span:first-child'
);

const stickyAddToCartText = currentRoot.querySelector(
  '.sticky-add-to-cart__button .add-to-cart-text__content > span:first-child'
);

if (mainAddToCartText) {
  mainAddToCartText.textContent = purchaseButtonText;
}

if (stickyAddToCartText) {
  stickyAddToCartText.textContent = purchaseButtonText;
}
  if (isMadeToOrder) {
    notice.setAttribute('data-made-to-order', 'true');
  } else {
    notice.removeAttribute('data-made-to-order');
  }
};

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }


  // Scoped pilot: extras are regular products, submitted with the blanket in one request.
  #addonBusy = false;

  handleAddonChange() {
    const extras = this.querySelector('[data-ra-addons]');
    if (!extras) return;
    const embroidery = extras.querySelector('[data-addon-embroidery]');
    const details = extras.querySelector('[data-addon-fields]');
    const name = extras.querySelector('[data-addon-name]');
    const font = extras.querySelector('[data-addon-font]');
    const selected = Boolean(embroidery?.checked);
    if (details) details.hidden = !selected;
    if (name) { name.disabled = !selected; name.required = selected; name.setCustomValidity(''); }
    if (font) font.disabled = !selected;
    const messageSelected = Boolean(extras.querySelector('[data-addon-message]')?.checked);
    const messageFields = extras.querySelector('[data-addon-message-fields]');
    const messageText = extras.querySelector('[data-addon-message-text]');
    if (messageFields) messageFields.hidden = !messageSelected;
    if (messageText) messageText.disabled = !messageSelected;
    const anySelected = Boolean(extras.querySelector('input[type="checkbox"]:checked'));
    this.toggleAttribute('data-ra-extras-selected', anySelected);
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();
    const extras = this.querySelector('[data-ra-addons]');
    if (extras) {
      if (this.#addonBusy) return;
      if (this.#variantChangeInProgress) {
        this.#setLiveRegionText(extras.dataset.waitMessage || '');
        return;
      }
      const name = extras.querySelector('[data-addon-name]');
      if (name && !name.disabled) {
        const valid = name.value.trim().length > 0 && name.value.trim().length <= 10;
        name.setCustomValidity(valid ? '' : extras.dataset.nameError);
        if (!name.reportValidity()) return;
      }
      if (!this.querySelector('form')?.reportValidity()) return;
    }

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

const formData = new FormData(form);

if (overrideVariantId) {
  formData.set('id', overrideVariantId);
}

if (overrideQuantity !== undefined) {
  formData.set('quantity', overrideQuantity.toString());
}

const variantId = String(formData.get('id') ?? '');
const itemCount =
  Number(formData.get('quantity')) ||
  Number(this.dataset.quantityDefault) ||
  1;

const currentRoot =
  this.closest('.shopify-section, dialog, product-card') ?? document;
const madeToOrderContainer = currentRoot.querySelector(
  '[data-made-to-order-notice]'
);

const inventoryQuantity = Math.max(
  0,
  Number(madeToOrderContainer?.dataset.inventoryQuantity ?? 0)
);

const existingCartLineKeys = new Set();
let cartQuantity = 0;

const matchingCartLines = document.querySelectorAll(
  `[data-made-to-order-cart-line][data-variant-id="${variantId}"]`
);

for (const cartLine of matchingCartLines) {
  const fulfillment = cartLine.dataset.fulfillment ?? '';

  if (fulfillment.startsWith('Made to order')) continue;

  const lineKey = cartLine.dataset.key;

  if (!lineKey || existingCartLineKeys.has(lineKey)) continue;

  existingCartLineKeys.add(lineKey);

  const cartLineQuantityInput = cartLine.querySelector(
    'input[ref="quantityInput"]'
  );

  cartQuantity += Math.max(
    0,
    Number(cartLineQuantityInput?.value ?? 0)
  );
}

const availableToShipQuantity = Math.max(
  0,
  inventoryQuantity - cartQuantity
);

const inStockQuantity = Math.min(
  itemCount,
  availableToShipQuantity
);

const madeToOrderQuantity =
  itemCount - inStockQuantity;

const shouldSplitItems =
  madeToOrderContainer?.dataset.inventoryManagement === 'shopify' &&
  madeToOrderContainer?.dataset.inventoryPolicy === 'continue' &&
  madeToOrderQuantity > 0;

const baseProperties = {};

for (const [key, value] of formData.entries()) {
  const propertyMatch = key.match(/^properties\[(.+)\]$/);

  if (
    propertyMatch &&
    typeof value === 'string' &&
    propertyMatch[1] !== 'Made to Order' &&
    propertyMatch[1] !== 'Fulfillment'
  ) {
    baseProperties[propertyMatch[1]] = value;
  }
}

const sellingPlan = formData.get('selling_plan');
const itemsToAdd = [];

if (shouldSplitItems) {
  if (inStockQuantity > 0) {
    itemsToAdd.push({
      id: variantId,
      quantity: inStockQuantity,
      properties: {
        ...baseProperties,
        Fulfillment: 'Ready to ship — ships in 2–3 business days',
      },
      ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
    });
  }

  itemsToAdd.push({
    id: variantId,
    quantity: madeToOrderQuantity,
    properties: {
      ...baseProperties,
      Fulfillment:
        'Made to order — created especially for you in about 3 weeks',
    },
    ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
  });
} else {
  itemsToAdd.push({
    id: variantId,
    quantity: itemCount,
    properties: baseProperties,
    ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
  });
}


const extras = this.querySelector('[data-ra-addons]');
const embroideryCheck = extras?.querySelector('[data-addon-embroidery]');
const giftCheck = extras?.querySelector('[data-addon-gift]');
const hasExtras = Boolean(embroideryCheck?.checked || giftCheck?.checked);
if (hasExtras) {
  const group = crypto.randomUUID();
  const blanketTitle = extras.dataset.blanketTitle || '';
  const name = extras.querySelector('[data-addon-name]')?.value.trim() || '';
  const font = extras.querySelector('[data-addon-font]');
  const fontLabel = font?.selectedOptions[0]?.dataset.font || '';
  for (const item of itemsToAdd) {
    item.properties._ra_bundle = group;
    if (embroideryCheck?.checked) {
      item.properties['Embroidery name'] = name;
      item.properties['Embroidery font'] = fontLabel;
    }
    if (giftCheck?.checked) item.properties['Gift packaging'] = 'Gift box';
  }
  const linkedProperties = {
    _ra_bundle: group,
    'For blanket': blanketTitle,
    _ra_blanket_variant: variantId,
  };
  if (embroideryCheck?.checked && font?.value) {
    itemsToAdd.push({id: font.value, quantity: itemCount,
      properties: {...linkedProperties, 'Name': name, 'Font': fontLabel}});
  }
  if (giftCheck?.checked && giftCheck.dataset.variantId) {
    itemsToAdd.push({id: giftCheck.dataset.variantId, quantity: itemCount, properties: linkedProperties});
  }
}
if (extras) {
  this.#addonBusy = true;
  for (const container of allAddToCartContainers) container.disable();
}

const cartItemsComponents =
  document.querySelectorAll('cart-items-component');
const cartItemComponentsSectionIds = [];

cartItemsComponents.forEach((item) => {
  if (
    item instanceof HTMLElement &&
    item.dataset.sectionId
  ) {
    cartItemComponentsSectionIds.push(
      item.dataset.sectionId
    );
  }
});

const deferredEventPromise =
  CartLinesUpdateEvent.createPromise();

this.dispatchEvent(
  new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: itemsToAdd.map((item) => ({
      merchandiseId: item.id,
      quantity: item.quantity,
    })),
    promise: deferredEventPromise.promise,
  })
);

let fetchCfg;

if (shouldSplitItems || hasExtras) {
  fetchCfg = fetchConfig('javascript', {
    body: JSON.stringify({
      items: itemsToAdd,
      sections: cartItemComponentsSectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  fetchCfg.headers = {
    ...fetchCfg.headers,
    'Content-Type': 'application/json',
  };
} else {
  formData.append(
    'sections',
    cartItemComponentsSectionIds.join(',')
  );

  fetchCfg = fetchConfig('javascript', {
    body: formData,
  });
}  


    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#refreshCart()
            .then((ajaxCart) => {
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                  sections: response.sections,
                  didError: false,
                },
              });

              if (this.#getVariantIdInput()) {
                this.#updateCartQuantity(ajaxCart);
              }
const quantitySelector = this.refs.quantitySelector;
const minimumQuantity =
  quantitySelector?.getCurrentValues?.().min ?? 1;

quantitySelector?.setValue?.(
  minimumQuantity.toString()
);
quantitySelector?.onQuantityChange?.();
quantitySelector?.updateButtonStates?.();

this.#scheduleMadeToOrderSync();

              return ajaxCart;
            })
            .catch(deferredEventPromise.reject);
        }
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        if (extras) {
          this.#addonBusy = false;
          for (const container of allAddToCartContainers) container.enable();
        }
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount: totalQuantity,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(response.message));
          }
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
        this.#updateCartQuantity(cart);
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;

    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';

      // Refresh the sample product's shipping estimate from server-rendered inventory.
      if (generation === this.#variantChangeGeneration) {
        const shippingStatus = this.querySelector('[data-ra-shipping-status]');
        const nextShippingStatus = html.querySelector('[data-ra-shipping-status]');
        if (shippingStatus && nextShippingStatus) morph(shippingStatus, nextShippingStatus);
        const sizeNote = this.closest('.shopify-section')?.querySelector('.ra-lovey-size-note');
        const nextSizeNote = html.querySelector('.ra-lovey-size-note');
        if (sizeNote && nextSizeNote) sizeNote.hidden = nextSizeNote.hasAttribute('hidden');
      }

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;
this.#scheduleMadeToOrderSync();

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;

        // Drain any queued add-to-cart requests that accumulated during the variant change
        if (this.#addToCartQueue.length > 0) {
          const queuedItems = [...this.#addToCartQueue];
          this.#addToCartQueue = [];
          this.#processBatchAddToCart(queuedItems);
        }
      }
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
